/**
 * Shared technician UI: building device list, add-device modal, read-only detail panel.
 * Used on inspection checklist and job hub so web matches mobile Jobs → Devices behaviour.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiRequest, buildApiUrl } from '../../api/client';
import { DEVICE_TYPES, DEVICE_PLACEMENTS, formatDevicePlacementLabel, formatDeviceTypeLabel } from '../../pages/admin/devices/deviceTypes';
import '../../pages/admin/quotes/quote-generator.css';

type DeviceDetail = {
    _id?: string;
    type?: string;
    manufacturer?: string;
    modelName?: string;
    modelNumber?: string;
    serialNumber?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
    lastInspectedAt?: string | null;
    lastInspectionResult?: string;
    photos?: string[];
    notes?: string;
};

export type BuildingDeviceRow = {
    _id: string;
    type?: string;
    manufacturer?: string;
    modelName?: string;
    modelNumber?: string;
    serialNumber?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
    lastInspectedAt?: string | null;
    lastInspectionResult?: string;
};

type AddDeviceForm = {
    type: string;
    manufacturer: string;
    modelName: string;
    modelNumber: string;
    serialNumber: string;
    installationLocation: string;
    floor: string;
    zone: string;
    placement: string;
    installationDate: string;
    notes: string;
};

function emptyForm(): AddDeviceForm {
    return {
        type: DEVICE_TYPES[0],
        manufacturer: '',
        modelName: '',
        modelNumber: '',
        serialNumber: '',
        installationLocation: '',
        floor: '',
        zone: '',
        placement: 'unspecified',
        installationDate: '',
        notes: ''
    };
}

const PHOTO_INPUT_ID = 'technician-shared-device-photos';

async function uploadImage(file: File): Promise<string | null> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(buildApiUrl('/api/upload'), { method: 'POST', body: formData });
    const result = await response.json();
    if (result.success && result.data?.url) return result.data.url as string;
    return null;
}

type Props = {
    clientId: string | null;
    /** Raised when registering a device fails (parent may show toast / global banner). */
    onError?: (message: string) => void;
    /** When set, POST /api/devices appends standardized checklist rows to this inspection. */
    appendInspectionId?: string | null;
    /** Server returns refreshed inspection document when append succeeds. */
    onInspectionUpdated?: (inspection: unknown) => void;
};

export default function TechnicianBuildingDevicesBlock({
    clientId,
    onError,
    appendInspectionId,
    onInspectionUpdated
}: Props): React.JSX.Element {
    const location = useLocation();
    const [rows, setRows] = useState<BuildingDeviceRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [successNotice, setSuccessNotice] = useState<string | null>(null);
    const [panel, setPanel] = useState<DeviceDetail | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState<AddDeviceForm>(emptyForm());
    const [saveBusy, setSaveBusy] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [zoneFilter, setZoneFilter] = useState('all');
    const [resultFilter, setResultFilter] = useState('all');
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const reload = useCallback(async (cid: string) => {
        setLoading(true);
        setErr(null);
        try {
            const res = await apiRequest<{ success: boolean; data: BuildingDeviceRow[] }>(
                `/api/devices?clientId=${encodeURIComponent(cid)}`
            );
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (e: unknown) {
            setRows([]);
            const m = e instanceof Error ? e.message : 'Could not load devices.';
            setErr(m);
            onError?.(m);
        } finally {
            setLoading(false);
        }
    }, [onError]);

    useEffect(() => {
        if (!clientId) {
            setRows([]);
            setErr(null);
            return;
        }
        reload(clientId).catch(() => undefined);
        // Reload when navigating back here (e.g. after finishing an inspection) so last-result columns stay current.
    }, [clientId, reload, location.key]);

    const zoneOptions = useMemo(() => {
        const values = Array.from(
            new Set(
                rows
                    .map((r) => (r.zone || '').trim())
                    .filter(Boolean)
            )
        );
        return values.sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const terms = q.split(/\s+/).filter(Boolean);
        return rows.filter((d) => {
            if (typeFilter !== 'all' && (d.type || '') !== typeFilter) return false;
            if (zoneFilter !== 'all' && (d.zone || '').trim() !== zoneFilter) return false;
            if (resultFilter !== 'all') {
                const normalized = (d.lastInspectionResult || '').toLowerCase();
                if (resultFilter === 'untested') {
                    if (normalized === 'pass' || normalized === 'fail') return false;
                } else if (normalized !== resultFilter) {
                    return false;
                }
            }
            if (terms.length === 0) return true;
            const hay =
                `${formatDeviceTypeLabel(d.type)} ${d.manufacturer || ''} ${d.modelName || ''} ${d.modelNumber || ''} ${d.serialNumber || ''} ${d.installationLocation || ''} ${d.floor || ''} ${d.zone || ''}`.toLowerCase();
            return terms.every((t) => hay.includes(t));
        });
    }, [rows, search, typeFilter, zoneFilter, resultFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const pagedRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, page]);

    useEffect(() => {
        setPage(1);
    }, [search, typeFilter, zoneFilter, resultFilter]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    async function openRow(id: string) {
        try {
            const res = await apiRequest<{ success: boolean; data: DeviceDetail }>(`/api/devices/${id}`);
            setPanel(res.data);
        } catch (e: unknown) {
            const m = e instanceof Error ? e.message : 'Could not load device.';
            setErr(m);
            onError?.(m);
        }
    }

    function openAdd() {
        setForm(emptyForm());
        setSuccessNotice(null);
        setAddOpen(true);
    }

    async function submitNew() {
        if (!clientId) return;
        if (!form.installationLocation.trim()) {
            const m = 'Installation location is required.';
            setErr(m);
            onError?.(m);
            return;
        }
        const input = document.getElementById(PHOTO_INPUT_ID) as HTMLInputElement | null;
        setSaveBusy(true);
        setErr(null);
        setSuccessNotice(null);
        try {
            const photos: string[] = [];
            const files = input?.files;
            if (files?.length) {
                for (let i = 0; i < files.length; i += 1) {
                    const url = await uploadImage(files[i]);
                    if (url) photos.push(url);
                }
            }
            const res = await apiRequest<{
                success?: boolean;
                data: unknown;
                inspection?: unknown;
            }>('/api/devices', {
                method: 'POST',
                json: {
                    clientId,
                    type: form.type,
                    manufacturer: form.manufacturer.trim(),
                    modelName: form.modelName.trim(),
                    modelNumber: form.modelNumber.trim(),
                    serialNumber: form.serialNumber.trim(),
                    installationLocation: form.installationLocation.trim(),
                    floor: form.floor.trim(),
                    zone: form.zone.trim(),
                    placement: form.placement || 'unspecified',
                    installationDate: form.installationDate.trim() || undefined,
                    notes: form.notes.trim(),
                    photos,
                    ...(appendInspectionId ? { appendToInspectionId: appendInspectionId } : {})
                }
            });
            if (res.inspection) {
                onInspectionUpdated?.(res.inspection);
            }
            // Always refetch registry: append flow returns inspection but skips listing unless we reload here.
            await reload(clientId);
            setAddOpen(false);
            setForm(emptyForm());
            if (input) input.value = '';
            setSuccessNotice(
                appendInspectionId
                    ? 'Device registered — checklist updated for this inspection.'
                    : 'Device registered. It appears below and on the admin client record.'
            );
        } catch (e: unknown) {
            const m = e instanceof Error ? e.message : 'Could not save device.';
            setErr(m);
            onError?.(m);
        } finally {
            setSaveBusy(false);
        }
    }

    if (!clientId) {
        return (
            <div className="banner banner-error" style={{ marginBottom: 16 }}>
                Building devices need a saved client on this record. Ask the office to link the inspection or job to a client.
            </div>
        );
    }

    return (
        <>
            <div className="quote-card tbd-card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                    <div className="quote-section-title" style={{ margin: 0 }}>
                        Registered devices at this site
                    </div>
                    <button type="button" className="btn-primary" onClick={openAdd} style={{ padding: '10px 18px', fontSize: '0.92rem' }}>
                        + Add device
                    </button>
                </div>
                {successNotice ? (
                    <div className="banner banner-success" style={{ marginBottom: 14 }}>
                        {successNotice}
                    </div>
                ) : null}
                {err ? (
                    <div className="banner banner-error" style={{ marginBottom: 12 }}>
                        {err}
                    </div>
                ) : null}
                <p style={{ color: 'var(--dash-muted)', fontSize: '0.88rem', marginTop: 0, marginBottom: 14, lineHeight: 1.5 }}>
                    Same as mobile: technicians can browse and register assets here (read-only list + add). Tap a row for details. Linked
                    checklist lines show a device chip on <strong>Checklist</strong>.
                </p>
                <div className="tbd-filter-grid">
                    <input
                        className="form-input tbd-filter-search"
                        placeholder="Search type, location, serial, model..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select className="form-input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                        <option value="all">All device types</option>
                        {DEVICE_TYPES.map((t) => (
                            <option key={t} value={t}>
                                {formatDeviceTypeLabel(t)}
                            </option>
                        ))}
                    </select>
                    <select className="form-input" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
                        <option value="all">All zones</option>
                        {zoneOptions.map((z) => (
                            <option key={z} value={z}>
                                {z}
                            </option>
                        ))}
                    </select>
                    <select className="form-input" value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
                        <option value="all">All last results</option>
                        <option value="pass">Pass</option>
                        <option value="fail">Fail</option>
                        <option value="untested">Untested</option>
                    </select>
                </div>
                {loading ? <p style={{ color: 'var(--dash-muted)' }}>Loading devices…</p> : null}
                {!loading && !err && rows.length === 0 ? (
                    <p style={{ color: 'var(--dash-muted)', margin: 0 }}>No active devices registered for this building yet.</p>
                ) : null}
                {!loading && !err && rows.length > 0 && filteredRows.length === 0 ? (
                    <p style={{ color: 'var(--dash-muted)', margin: 0 }}>No devices match the current filters.</p>
                ) : null}
                {filteredRows.length > 0 ? (
                    <div className="tbd-table-wrap" style={{ overflowX: 'auto' }}>
                        <table className="tbd-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.08)', textAlign: 'left', color: 'var(--dash-muted)' }}>
                                    <th style={{ padding: '8px 6px' }}>Type</th>
                                    <th style={{ padding: '8px 6px' }}>Model</th>
                                    <th style={{ padding: '8px 6px' }}>Location</th>
                                    <th style={{ padding: '8px 6px' }}>Floor</th>
                                    <th style={{ padding: '8px 6px' }}>Serial</th>
                                    <th style={{ padding: '8px 6px' }}>Last inspected</th>
                                    <th style={{ padding: '8px 6px' }}>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedRows.map((d) => {
                                    const fail = d.lastInspectionResult === 'fail';
                                    return (
                                        <tr
                                            key={d._id}
                                            style={{
                                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                                cursor: 'pointer',
                                                background: fail ? 'rgba(192,57,43,0.06)' : undefined
                                            }}
                                            onClick={() => void openRow(d._id)}
                                        >
                                            <td style={{ padding: '10px 6px', fontWeight: 700 }}>{formatDeviceTypeLabel(d.type)}</td>
                                            <td style={{ padding: '10px 6px' }}>
                                                {[d.manufacturer, d.modelName].filter(Boolean).join(' · ') || '—'}
                                            </td>
                                            <td style={{ padding: '10px 6px' }}>{d.installationLocation || '—'}</td>
                                            <td style={{ padding: '10px 6px' }}>{d.floor || '—'}</td>
                                            <td style={{ padding: '10px 6px', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                {d.serialNumber || '—'}
                                            </td>
                                            <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                                                {d.lastInspectedAt ? new Date(d.lastInspectedAt).toLocaleDateString() : '—'}
                                            </td>
                                            <td style={{ padding: '10px 6px', fontWeight: 800, color: fail ? '#c0392b' : '#2e8a57' }}>
                                                {d.lastInspectionResult === 'pass' ? 'Pass' : d.lastInspectionResult === 'fail' ? 'Fail' : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
                {filteredRows.length > 0 ? (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--dash-muted)' }}>
                            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredRows.length)} of {filteredRows.length}
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                                Prev
                            </button>
                            <span style={{ alignSelf: 'center', fontSize: '0.82rem', color: 'var(--dash-muted)' }}>
                                Page {page} / {totalPages}
                            </span>
                            <button type="button" className="btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                                Next
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>

            {panel ? (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: 'min(380px, 100vw)',
                        height: '100vh',
                        background: '#ffffff',
                        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
                        zIndex: 45,
                        padding: '72px 20px 24px',
                        overflowY: 'auto',
                        borderLeft: '1px solid rgba(0,0,0,0.08)'
                    }}
                >
                    <button type="button" className="btn-secondary" style={{ marginBottom: 16 }} onClick={() => setPanel(null)}>
                        Close panel
                    </button>
                    <h2 style={{ fontSize: '1.05rem', marginBottom: 8 }}>{formatDeviceTypeLabel(panel.type)}</h2>
                    <p style={{ margin: '0 0 12px', color: 'var(--dash-muted)', fontSize: '0.88rem' }}>Registered asset (read-only).</p>
                    <dl style={{ display: 'grid', gridTemplateColumns: '118px 1fr', gap: '6px 12px', fontSize: '0.88rem' }}>
                        <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Model</dt>
                        <dd style={{ margin: 0 }}>{`${panel.manufacturer || ''} ${panel.modelName || ''}`.trim() || '—'}</dd>
                        <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}># / serial</dt>
                        <dd style={{ margin: 0 }}>{[panel.modelNumber, panel.serialNumber].filter(Boolean).join(' • ') || '—'}</dd>
                        <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Location</dt>
                        <dd style={{ margin: 0 }}>{panel.installationLocation || '—'}</dd>
                        <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Floor</dt>
                        <dd style={{ margin: 0 }}>{panel.floor || '—'}</dd>
                        <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Zone</dt>
                        <dd style={{ margin: 0 }}>{panel.zone || '—'}</dd>
                        <dt style={{ fontWeight: 700, color: 'var(--dash-muted)' }}>Last result</dt>
                        <dd style={{ margin: 0, fontWeight: 800 }}>
                            {(panel.lastInspectionResult || 'not tested').replace(/_/g, ' ')}
                            {panel.lastInspectedAt ? ` · ${new Date(panel.lastInspectedAt).toLocaleDateString()}` : ''}
                        </dd>
                    </dl>
                    {panel.notes ? (
                        <>
                            <h3 style={{ fontSize: '0.85rem', marginTop: 16, marginBottom: 6 }}>Notes</h3>
                            <p style={{ fontSize: '0.88rem', lineHeight: 1.5, margin: 0 }}>{panel.notes}</p>
                        </>
                    ) : null}
                    {panel.photos && panel.photos.length > 0 ? (
                        <>
                            <h3 style={{ fontSize: '0.85rem', marginTop: 16, marginBottom: 8 }}>Photos</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                {panel.photos.map((url) => (
                                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                        <img
                                            src={buildApiUrl(url)}
                                            alt=""
                                            style={{ width: 100, height: 72, objectFit: 'cover', borderRadius: 8 }}
                                        />
                                    </a>
                                ))}
                            </div>
                        </>
                    ) : null}
                </div>
            ) : null}

            {addOpen ? (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="technician-shared-add-device-title"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 20
                    }}
                >
                    <div className="quote-card" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
                        <h2 id="technician-shared-add-device-title" style={{ fontSize: '1.25rem', marginBottom: 6 }}>
                            Register device
                        </h2>
                        <p style={{ color: 'var(--dash-muted)', fontSize: '0.88rem', marginTop: 0, marginBottom: 16 }}>
                            Appears immediately for this building (same as mobile).
                        </p>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor="tbd-type">
                                    Device type
                                </label>
                                <select
                                    id="tbd-type"
                                    className="form-input"
                                    value={form.type}
                                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                                >
                                    {DEVICE_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                            {formatDeviceTypeLabel(t)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="tbd-mfr">
                                    Manufacturer
                                </label>
                                <input id="tbd-mfr" className="form-input" value={form.manufacturer} onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="tbd-mname">
                                    Model name
                                </label>
                                <input id="tbd-mname" className="form-input" value={form.modelName} onChange={(e) => setForm((f) => ({ ...f, modelName: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="tbd-mnum">
                                    Model number
                                </label>
                                <input id="tbd-mnum" className="form-input" value={form.modelNumber} onChange={(e) => setForm((f) => ({ ...f, modelNumber: e.target.value }))} />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor="tbd-serial">
                                    Serial number
                                </label>
                                <input id="tbd-serial" className="form-input" value={form.serialNumber} onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))} />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor="tbd-loc">
                                    Installation location *
                                </label>
                                <input
                                    id="tbd-loc"
                                    className="form-input"
                                    placeholder="Floor 2, Room 201, East wall"
                                    value={form.installationLocation}
                                    onChange={(e) => setForm((f) => ({ ...f, installationLocation: e.target.value }))}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="tbd-floor">
                                    Floor
                                </label>
                                <input id="tbd-floor" className="form-input" value={form.floor} onChange={(e) => setForm((f) => ({ ...f, floor: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="tbd-zone">
                                    Zone
                                </label>
                                <input id="tbd-zone" className="form-input" value={form.zone} onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))} />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor="tbd-placement">
                                    Area (annual report)
                                </label>
                                <select
                                    id="tbd-placement"
                                    className="form-input"
                                    value={form.placement}
                                    onChange={(e) => setForm((f) => ({ ...f, placement: e.target.value }))}
                                >
                                    {DEVICE_PLACEMENTS.map((p) => (
                                        <option key={p} value={p}>
                                            {formatDevicePlacementLabel(p)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor="tbd-date">
                                    Installation date
                                </label>
                                <input
                                    id="tbd-date"
                                    type="date"
                                    className="form-input"
                                    value={form.installationDate}
                                    onChange={(e) => setForm((f) => ({ ...f, installationDate: e.target.value }))}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor={PHOTO_INPUT_ID}>
                                    Photos (optional, multiple)
                                </label>
                                <input id={PHOTO_INPUT_ID} type="file" accept="image/*" multiple />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label" htmlFor="tbd-notes">
                                    Notes
                                </label>
                                <textarea id="tbd-notes" className="form-textarea" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 18 }}>
                            <button
                                type="button"
                                className="btn-secondary"
                                disabled={saveBusy}
                                onClick={() => {
                                    setAddOpen(false);
                                    setForm(emptyForm());
                                    const el = document.getElementById(PHOTO_INPUT_ID) as HTMLInputElement | null;
                                    if (el) el.value = '';
                                }}
                            >
                                Cancel
                            </button>
                            <button type="button" className="btn-primary" disabled={saveBusy} onClick={() => void submitNew()}>
                                {saveBusy ? 'Saving…' : 'Save device'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
